import React from 'react'
import ReactDOM from 'react-dom'
import { HashRouter } from 'react-router-dom'
// eslint-disable-next-line import/no-cycle
import { App } from './App'

/* aws */
import Amplify from 'aws-amplify'
import config from './config'
import TagManager from 'react-gtm-module'
import firebase from 'firebase'
import rootStore from './stores/rootStore'
import ReactPixel from 'react-facebook-pixel'
import './reusableUtils/ArrayExts'

/* Sentry */
import { Sentry } from './sentry'

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: config.cognito.REGION,
    userPoolId: config.cognito.USER_POOL_ID,
    userPoolWebClientId: config.cognito.APP_CLIENT_ID,
    identityPoolId: config.cognito.IDENTITY_POOL_ID
  },
  Storage: {
    region: config.s3.REGION,
    bucket: config.s3.BUCKET,
    identityPoolId: config.cognito.IDENTITY_POOL_ID
  }
})

// Firebase Analytics
// SECURITY: All API keys moved to environment variables to prevent exposure in bundle
const firebaseConfig = {
  apiKey: config.firebase.API_KEY,
  authDomain: config.firebase.AUTH_DOMAIN,
  databaseURL: config.firebase.DATABASE_URL,
  projectId: config.firebase.PROJECT_ID,
  storageBucket: config.firebase.STORAGE_BUCKET,
  messagingSenderId: config.firebase.MESSAGING_SENDER_ID,
  appId: config.firebase.APP_ID,
  measurementId: config.firebase.MEASUREMENT_ID
}

// Initialize Firebase only if all required config values are present
let analytics: firebase.analytics.Analytics | null = null
if (firebaseConfig.apiKey && firebaseConfig.projectId) {
  try {
    firebase.initializeApp(firebaseConfig)
    // check if user disabled analytics
    analytics = firebase.analytics()
  } catch (error) {
    console.warn('Firebase initialization failed:', error)
    analytics = null
  }
}

const options = {
  autoConfig: true,
  debug: false
}

// SECURITY: Facebook Pixel ID moved to environment variable
if (config.analytics.FACEBOOK_PIXEL_ID) {
  ReactPixel.init(config.analytics.FACEBOOK_PIXEL_ID, undefined, options)
  ReactPixel.grantConsent()
}

if (analytics) {
  analytics.setAnalyticsCollectionEnabled(true)
}

// Only run analytics setup in browser environment (not in tests)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  if (!rootStore.userStore.authenticatedUser?.enabledAnalytics) {
    if (analytics) {
      analytics.setAnalyticsCollectionEnabled(false)
    }
    ReactPixel.revokeConsent()
  }
}

// SECURITY: GTM ID moved to environment variable
const tagManagerArgs = {
  gtmId: config.analytics.GTM_ID || ''
}

// Only initialize GTM if ID is provided
if (tagManagerArgs.gtmId) {
  TagManager.initialize(tagManagerArgs)
}

// Fix URL format for HashRouter: convert /path#/ to /#/path
// This handles cases where URLs are shared without proper hash routing format
const fixHashRouterUrl = () => {
  const { pathname, hash, search, origin } = window.location
  // If there's a meaningful path and the hash is empty or just "#/" or "#", redirect to hash-based URL
  if (pathname !== '/' && (!hash || hash === '#' || hash === '#/')) {
    const correctUrl = `${origin}/#${pathname}${search}`
    window.location.replace(correctUrl)
    return true
  }
  return false
}

// Run the fix before rendering - if redirecting, don't render yet
if (fixHashRouterUrl()) {
  // Page will reload with correct URL, no need to render
} else {
  ReactDOM.render(
    <Sentry.ErrorBoundary>
      <HashRouter>
        <App />
      </HashRouter>
    </Sentry.ErrorBoundary>,
    document.getElementById('root')
  )
}

export { analytics, TagManager }
